import {
    applyThemeColorsToCharts,
    BarChartVM,
    BarDataType,
    ChartTableData,
    PieDataType,
} from '../../dashboard/chart-vm';
import { AnalyzeFilters, UserFilter, getApiNoteFilterValue, LayerFilter } from '../analyze-filters';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { Layer } from '@/core/models/layer';
import { LoadingService } from '@/core/services/loading.service';
import { ReportService } from '@/core/services/open-api/report.service';
import { LayerService } from '@/core/services/open-api/layer.service';
import { ChartOptions, ChartVM } from '../../dashboard/chart-vm';
import { TranslateService } from '@ngx-translate/core';
import { DateRangeHelper } from '@/core/services/utils/date-range-helper';
import { ChartReportAdapterService } from '@/core/services/chart-report-adapter.service';
import { ReportRequestForm, ReportResponseForm, GroupByOptions, responseToStringArray } from '@/core/models/report';
import { ActivityFiltersForm } from '@/core/models/chart';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { UserService } from '@/core/services/open-api/user.service';
import { CurrentUser } from '@/core/models/user';
import { UserPermission } from '@/core/models/user-permission';
import { LazyLoadEvent, SelectItem } from 'primeng/api';
import { OptionItemValue } from '@/core/models/assignment-options.interface';
import { GroupForm } from '@/core/models/group-form';
import { SharedFunctions } from '@/core/utils/shared-functions';
import { ThemeService } from '@/core/services/theme.service';
import { ActivityService } from '@/core/services/open-api/activity.service';
import { InitializationManager } from '@/core/utils/initialization-manager';
import { PageDataState } from '@/common/shared/shared-types';
import { LocalStorageService } from '@/core/services/utils/local-storage.service';

// note: ts-expect-error import fails with ts, because the module has no exported member "mkConfig", etc...
// import { mkConfig, generateCsv, download } from 'export-to-csv';

@Component({
    selector: 'app-analyze-overview',
    templateUrl: './analyze-overview.component.html',
    styleUrls: ['./analyze-overview.component.scss'],
    standalone: false,
})
export class AnalyzeOverviewComponent implements OnInit, OnDestroy {
    @Input() allUsers: UserFilter[] = [];
    @Input({ required: true }) filters!: AnalyzeFilters;
    @Input({ required: true }) filterSubmit$!: Observable<AnalyzeFilters>;
    @Output() onHasDataChanged = new EventEmitter<boolean>();
    @ViewChild('analyzeOverview') analyzeOverviewDiv!: ElementRef;
    barData?: BarDataType;
    pieData?: PieDataType;
    tableData: ChartTableData[] = [];
    reportData?: ReportResponseForm;
    stackedOptions = ChartOptions.stackedOptionsWithLegend;
    pieOptions = ChartOptions.pieOptions;
    barChartHeight: string = '20vh';
    pieChartHeight: string = '20vh';
    // TODO group by options should be refactored and use optionValue instead of storing the whole option object
    // group by:
    tableFirstGroupOptions: GroupBySelect[] = [];
    tableSecondGroupOptions: GroupBySelect[] = [];
    tableFirstGroupSelected?: GroupBySelect;
    tableSecondGroupSelected?: GroupBySelect;
    barchartGroupOptions: GroupBySelect[] = [];
    barchartGroupSelected?: GroupBySelect;
    barChartIntervalOptions: GroupBySelect[] = [];
    barChartIntervalSelected?: GroupBySelect;
    totalTime: number = 0;
    paginationRows: number = 25;
    licenseStatePro = false;
    private hasReportData: boolean = false;
    private hasActivities: boolean = false;
    private subscriptions = new Subscription();
    dataState: PageDataState = 'loading';
    currentUser?: CurrentUser;
    groupTableAllExpanded: boolean = false;
    private layers: Layer[] = [];
    private initMgr: InitializationManager;

    constructor(
        private readonly reportService: ReportService,
        private readonly activityService: ActivityService,
        private readonly layerService: LayerService,
        private readonly loadingService: LoadingService,
        private readonly userService: UserService,
        private readonly translate: TranslateService,
        private readonly chartReportAdapter: ChartReportAdapterService,
        private readonly themeService: ThemeService,
    ) {
        this.initMgr = new InitializationManager(this.loadingService, this.constructor.name);
    }

    ngOnInit(): void {
        this.initMgr
            .addDependency(this.filterSubmit$, {
                name: 'filterSubmit$',
                callback: (filters: AnalyzeFilters) => (this.filters = filters),
            })
            .addDependency(this.userService.currentUser$, {
                name: 'currentUser$',
                callback: (currentUser: CurrentUser) => {
                    this.currentUser = currentUser;
                    this.licenseStatePro = this.currentUser.isPro();
                    this.chartReportAdapter.setUserSettings(this.currentUser.settings);
                },
            })
            .addDependency(this.layerService.layers$, {
                name: 'layers$',
                callback: (layers: Layer[]) => (this.layers = layers),
            })
            .onInit(async () => {
                this.initGroupByOptions();
                await this.load(false);
            })
            .onChange(async () => {
                await this.load(false);
            })
            .initialize();

        this.subscriptions.add(
            this.loadingService.isLoading$.subscribe((isLoading) => {
                if (isLoading) {
                    this.dataState = 'loading';
                } else if (this.hasReportData) {
                    this.dataState = 'has-data';
                } else if (this.hasActivities) {
                    this.dataState = 'empty-result';
                } else {
                    this.dataState = 'no-data';
                }
            }),
        );
    }

    private initGroupByOptions(): void {
        this.barchartGroupOptions = this.getBarchartGroupOptions(this.layers);
        this.barChartIntervalOptions = this.getBarchartIntervalOptions();
        this.tableFirstGroupOptions = this.getFirstGroupOptions(this.layers);
        this.tableSecondGroupOptions = this.getSecondGroupOptions();

        // TODO refactor group by options, extract logic, create proper validation for storing and loading
        let loadedFromStorage = false;
        const storedGroupByOptions = LocalStorageService.getAnalyzeGroupBys(this.currentUser!.id);
        if (storedGroupByOptions) {
            try {
                const tableFirst = this.tableFirstGroupOptions.find(
                    (opt) => opt.value === storedGroupByOptions.tableFirstGroup,
                );
                const tableSecond = this.tableSecondGroupOptions.find(
                    (opt) => opt.value === storedGroupByOptions.tableSecondGroup,
                );
                const bcGroup = this.barchartGroupOptions.find(
                    (opt) => opt.value === storedGroupByOptions.barchartGroup,
                );
                const bcInterval = this.barChartIntervalOptions.find(
                    (opt) => opt.value === storedGroupByOptions.barChartInterval,
                );
                if (tableFirst && tableSecond && bcGroup && bcInterval) {
                    this.tableFirstGroupSelected = tableFirst;
                    this.tableSecondGroupSelected = tableSecond;
                    this.barchartGroupSelected = bcGroup;
                    this.barChartIntervalSelected = bcInterval;
                    loadedFromStorage = true;
                    console.debug('GroupBy options loaded from local storage');
                }
            } catch {
                console.error('ERROR: GroupBy options from local storage were not valid. Using defaults!');
            }
        }

        if (!loadedFromStorage) {
            this.barchartGroupSelected = this.barchartGroupOptions[0];
            this.barChartIntervalSelected = this.barChartIntervalOptions[0];
            this.tableFirstGroupSelected = this.tableFirstGroupOptions[0];
            this.tableSecondGroupSelected = this.tableSecondGroupOptions[0];
        }
    }

    private storeGroupByOptions(): void {
        const groupByOptions: AnalyzeGroupBys = {
            tableFirstGroup: this.tableFirstGroupSelected!.value,
            tableSecondGroup: this.tableSecondGroupSelected!.value,
            barchartGroup: this.barchartGroupSelected!.value,
            barChartInterval: this.barChartIntervalSelected!.value,
        };
        LocalStorageService.storeAnalyzeGroupBys(this.currentUser!.id, groupByOptions);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.initMgr.destroy();
    }

    // stateful pagination row selection
    onPaginationChange(_event: LazyLoadEvent): void {}

    private getFilterRequestObject(): ActivityFiltersForm {
        let userIds =
            this.filters.selectedTeamFilter?.filter((cat) => cat?.type === 'members').map((cat) => cat.id) ?? [];
        if (userIds.length === 0) {
            userIds = this.allUsers.map((u) => u.id);
        }

        const groupsFilter =
            this.filters.selectedTeamFilter
                ?.filter((cat) => cat?.type === 'groups')
                .map((cat) => {
                    const groupOption = cat as OptionItemValue<GroupForm>;
                    return {
                        id: groupOption.id,
                        name: groupOption.name,
                    };
                }) ?? [];

        const tilesDto = SharedFunctions.getTilesDtoFromFilters(
            this.filters.selectedLayer1Filter,
            this.filters.selectedLayer2Filter,
            this.filters.selectedLayer3Filter,
        );

        const noteFilVal = getApiNoteFilterValue(this.filters.filterDescription, this.filters.isNoteAvailable);

        console.log('getFilterRequestObject: dateRange', this.filters.dateRange);

        return {
            note: noteFilVal,
            strictDate: false,
            userIds,
            tiles: tilesDto,
            billed: this.filters.isBilled,
            runningActivities: false,
            groups: groupsFilter,
            startDate: DateRangeHelper.getStartDate(this.filters.dateRange),
            endDate: DateRangeHelper.getEndDate(this.filters.dateRange),
        };
    }

    private getBarChartGroupBy(): GroupByOptions[] {
        const dateGroup =
            this.barChartIntervalSelected && this.barChartIntervalSelected.value != ''
                ? this.barChartIntervalSelected.value
                : 'day';
        const bcGroup =
            this.barchartGroupSelected && this.barchartGroupSelected.value != ''
                ? this.barchartGroupSelected.value
                : 'layer:1';
        return [bcGroup, dateGroup];
    }

    private getTableGroupBy(): GroupByOptions[] {
        const groupBy: GroupByOptions[] = [];
        if (this.tableFirstGroupSelected && this.tableFirstGroupSelected.value) {
            groupBy.push(this.tableFirstGroupSelected.value);
        } else {
            console.error('ERROR: GroupBy select initialization failed!');
            groupBy.push('layer:1');
        }
        if (
            this.tableFirstGroupSelected &&
            this.tableSecondGroupSelected &&
            this.tableFirstGroupSelected.value === this.tableSecondGroupSelected.value
        ) {
            console.error('ERROR: Same group option selected in both drop downs. Second option is being ignored.');
        } else if (this.tableSecondGroupSelected && this.tableSecondGroupSelected.value) {
            groupBy.push(this.tableSecondGroupSelected.value);
        }
        return groupBy;
    }

    private buildReportRequestBody(): ReportRequestForm {
        const chartFilter = this.getFilterRequestObject();
        console.log('buildReportRequestBody: chartFilter', chartFilter);
        const reportFilter = this.chartReportAdapter.getReportFilter(chartFilter);
        const groupByBarChart = this.getBarChartGroupBy();
        const groupByTable = this.getTableGroupBy();
        return {
            reports: [
                {
                    utcOffset: 'local',
                    filter: reportFilter,
                    groupBy: groupByBarChart,
                    format: {
                        user: 'default',
                    },
                },
                {
                    utcOffset: 'local',
                    filter: reportFilter,
                    groupBy: groupByTable,
                    format: {
                        user: 'default',
                    },
                },
            ],
        };
    }

    async load(showLoading = true): Promise<void> {
        if (showLoading) {
            this.loadingService.setLoadingState('data-loading', this.constructor.name);
        }
        this.storeGroupByOptions();

        const req = this.buildReportRequestBody();

        const response: ReportResponseForm = await firstValueFrom(this.reportService.getReport(req));
        this.reportData = response;
        const chartVm = this.chartReportAdapter.getChartVM(req, response);
        this.hasReportData = chartVm.totalSum != 0;
        if (this.hasReportData) {
            this.hasActivities = true;
        } else {
            const totalActivityCount = await this.activityService.getActivityCountAsync();
            this.hasActivities = totalActivityCount > 0;
        }
        this.setChart(chartVm);
        this.setPieChart(chartVm);
        this.setTable(chartVm);
        this.setTotalTime(chartVm);
        this.calcBarChartHeight();
        this.calcPieChartHeight();
        if (showLoading) {
            this.loadingService.setLoadingState('complete', this.constructor.name);
        }
    }

    private getGroupSelectOptions(layers: Layer[], withDates: boolean, onlyDates: boolean): GroupBySelect[] {
        const addDateGroupOptions = (selectOptions: GroupBySelect[]): void => {
            const dateDisabled = !(this.licenseStatePro || onlyDates);
            selectOptions.push({
                label: this.translate.instant('group_by_days'),
                value: 'day',
                disabled: dateDisabled,
            });
            selectOptions.push({
                label: this.translate.instant('group_by_weeks'),
                value: 'week',
                disabled: dateDisabled,
            });
            selectOptions.push({
                label: this.translate.instant('group_by_months'),
                value: 'month',
                disabled: dateDisabled,
            });
            selectOptions.push({
                label: this.translate.instant('group_by_years'),
                value: 'year',
                disabled: dateDisabled,
            });
        };

        const selectOptions: GroupBySelect[] = [];

        if (onlyDates) {
            addDateGroupOptions(selectOptions);
            return selectOptions;
        }

        const layerOptions = layers
            .filter((layers) => layers.visible)
            .map((layer: Layer) => {
                const layerSelect: GroupBySelect = {
                    label: layer.name ?? '',
                    value: `layer:${layer.index}` as GroupBySelectionOptions,
                };
                return layerSelect;
            });
        selectOptions.push(...layerOptions);

        const disabledFree = !this.licenseStatePro;

        selectOptions.push({ label: this.translate.instant('group_by_notes'), value: 'note', disabled: disabledFree });

        if (this.currentUser?.hasPermission(UserPermission.ACTIVITY_READ_TEAM)) {
            selectOptions.push({
                label: this.translate.instant('group_by_users'),
                value: 'user',
                disabled: disabledFree,
            });
            selectOptions.push({
                label: this.translate.instant('group_by_groups'),
                value: 'group',
                disabled: disabledFree,
            });
        }

        if (withDates) {
            addDateGroupOptions(selectOptions);
        }

        return selectOptions;
    }

    private getBarchartGroupOptions(layers: Layer[]): GroupBySelect[] {
        return this.getGroupSelectOptions(layers, false, false);
    }

    private getBarchartIntervalOptions(): GroupBySelect[] {
        return this.getGroupSelectOptions([], true, true);
    }

    private getFirstGroupOptions(layers: Layer[]): GroupBySelect[] {
        return this.getGroupSelectOptions(layers, true, false);
    }

    private getFirstSelectedOption(): GroupBySelect {
        return this.tableFirstGroupSelected ? this.tableFirstGroupSelected : this.tableFirstGroupOptions[0];
    }

    private getSecondGroupOptions(): GroupBySelect[] {
        const firstSel: GroupBySelect = this.getFirstSelectedOption();
        const options: GroupBySelect[] = this.tableFirstGroupOptions.filter((el) => el.label !== firstSel.label);
        const noneOption: GroupBySelect = { label: this.translate.instant('group_by_none'), value: '' };
        return [noneOption, ...options];
    }

    changeSecondGroup(_event: LayerFilter): void {
        void this.load();
    }

    changeFirstGroup(_event: LayerFilter): void {
        // reset second group option, when the same option was selected in the first dropdown
        const firstSel = this.getFirstSelectedOption();
        if (firstSel.value === this.tableSecondGroupSelected?.value) {
            this.tableSecondGroupSelected = this.tableSecondGroupOptions[0];
        }
        void this.load();
    }

    private setChart(chartVm: ChartVM): void {
        applyThemeColorsToCharts(this.themeService.colors());
        this.barData = {
            labels: chartVm.dates,
            datasets: chartVm.barChart.map((x) => {
                // TODO FIXME - what is the purpose of this mapping?
                // eslint-disable-next-line
                x.backgroundColor;
                return x;
            }),
        };

        this.onHasDataChanged.emit((this.barData?.datasets?.length ?? 0) > 0);
    }

    private setPieChart(chartVm: ChartVM): void {
        applyThemeColorsToCharts(this.themeService.colors());
        this.pieData = {
            labels: chartVm.pieChart.labels,
            datasets: [
                {
                    data: chartVm.pieChart.data,
                    backgroundColor: chartVm.pieChart.backgroundColor,
                    spacing: 0,
                    borderWidth: 1,
                    borderColor: this.themeService.colors().chartBorderColor,
                },
            ],
        };
    }

    private setTable(chartVm: ChartVM): void {
        this.tableData = chartVm.tableData;
    }

    private setTotalTime(chartVm: ChartVM): void {
        this.totalTime = chartVm.totalSum ?? 0;
    }

    getBorder(data: ChartTableData): string {
        return data.backgroundColor.toLowerCase() === '#ffffff' ? '1px solid #bbb' : data.backgroundColor;
    }

    changeBarChartGroup(): void {
        void this.load();
    }

    changeBarChartInterval(): void {
        void this.load();
    }

    calculateTotalDuration(group: string): number {
        let totalSeconds: number = 0;

        if (this.tableData) {
            for (const data of this.tableData) {
                if (data.group1 === group) {
                    totalSeconds += Number(data.durationSeconds);
                }
            }
        }
        return totalSeconds;
    }

    getFileName(): string {
        const timeStamp = new Date().getTime();
        return `timeBuzzer_report-${timeStamp}`;
    }

    exportTableToPdf = async (): Promise<void> => {
        if (!this.reportData || this.reportData.reports.length === 0) {
            return;
        }

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
        });

        const headers = this.reportData.reports[1].headers;
        const rows = this.reportData.reports[1].rows;

        // Generate the table
        autoTable(doc, {
            head: [headers],
            body: rows,
            styles: { fontSize: 10 },
            headStyles: { fillColor: [22, 160, 133] },
            margin: { top: 20 },
        });

        doc.save(this.getFileName() + '.pdf');
    };

    exportUsingXslxTools(fileType: 'xlsx' | 'csv'): void {
        if (!this.reportData || this.reportData.reports.length === 0) {
            return;
        }
        const fileName = this.getFileName();

        const data: string[][] = responseToStringArray(this.reportData.reports[1]);

        const worksheet = XLSX.utils.aoa_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

        if (fileType === 'csv') {
            const csv = XLSX.utils.sheet_to_csv(worksheet);
            const csvWithBom = '\uFEFF' + csv;
            const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, `${fileName}.csv`);
        } else if (fileType === 'xlsx') {
            const xlsxBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([xlsxBuffer], { type: 'application/octet-stream' });
            saveAs(blob, `${fileName}.xlsx`);
        }
    }

    exportTableToCsv = async (): Promise<void> => {
        this.exportUsingXslxTools('csv');
    };
    exportTableToExcel = async (): Promise<void> => {
        this.exportUsingXslxTools('xlsx');
    };

    expandCollapseAll(): void {
        this.groupTableAllExpanded = !this.groupTableAllExpanded;
    }

    calcBarChartHeight(): void {
        if (this.barData === undefined) {
            return;
        }
        const componentWidth = this.analyzeOverviewDiv?.nativeElement.offsetWidth;
        const width = componentWidth ? componentWidth * 0.9 : 1200;

        // Note: For the bar chart the labels are the dates. The options labels need to be used.
        const labels = this.barData.datasets.map((ds: BarChartVM) => ds.label);
        this.barChartHeight = this.calculateChartHeight(labels, width, 20, 60);
    }

    calcPieChartHeight(): void {
        if (this.pieData === undefined) {
            return;
        }
        this.pieChartHeight = this.calculateChartHeight(this.pieData.labels ?? [], 300, 20, 100);
    }

    /**
     * This method calculates the height of a p-chart element, so that it is able to display all labels. p-chart
     * renders the chart itself into the same canvas with a fixed height. This results in labels being cut of and chart
     * being shrunk. To work around the lack of p-chat offering separate height settings for the chart and the label area,
     * an approx. total height needs to be calculated depending on the number and size of the labels.
     *
     * @param labels The charts labels
     * @param elementWidth The width of the p-chart element
     * @param chartHeight The height the chart area should have in vh, e.g. 20vh
     * @param maxHeight The maximum heigh of the p-chart component, e.g. 50vh
     * @returns The approx. needed height of the p-chart element in vh, for example: 37vh
     */
    calculateChartHeight(labels: string[], elementWidth: number, chartHeight: number, maxHeight: number): string {
        console.debug(
            `calculateChartHeight() - width:${elementWidth}, height:${chartHeight}, maxHeight:${maxHeight}`,
            labels,
        );

        const LABEL_SIZE = 10; // The size of the label icon is approx. 8 chars + 2 chars spacing
        const labelCount = labels.length;
        const charCount = labels.reduce((sum: number, str: string) => sum + str.length, 0);

        const neededSize = labelCount * LABEL_SIZE + charCount;
        const maxCharsPerLine = Math.round(elementWidth / 6);
        const neededLines = Math.round(neededSize / maxCharsPerLine);

        const neededHeight = Math.ceil(neededLines * 2.15); // 2vh ~ 1 line of labels
        const labelAreaHeight = Math.min(neededHeight, maxHeight - chartHeight);

        return `${chartHeight + labelAreaHeight}vh`;
    }
}

type GroupBySelectionOptions = GroupByOptions | '';
type GroupBySelect = SelectItem<GroupBySelectionOptions>;

export type AnalyzeGroupBys = {
    tableFirstGroup: GroupBySelectionOptions;
    tableSecondGroup: GroupBySelectionOptions | '';
    barchartGroup: GroupBySelectionOptions;
    barChartInterval: GroupBySelectionOptions;
};
